import { PaletteMode } from '@mui/material';
import { createTheme, ThemeOptions } from '@mui/material/styles';

// assets

// project imports
import componentStyleOverrides from './comp-style-override';
import themePalette from './palette';
import { buildCustomShadows } from './shadows';
import themeTypography from './typography';

import { LayoutSlice } from '@/store/layout-slice';
import colors from '@/themes/colors';
import { darkColors } from '@/themes/dark-colors';
import hgcColors from '@/themes/hgc-colors';

type Client = 'telma' | 'hgc';

const getClientColors = (client: Client, mode: PaletteMode) => {
    if (client === 'hgc') {
        return hgcColors;
    }
    return mode === 'dark' ? darkColors : colors;
};

/**
 * Represent theme style and structure as per Material-UI
 * @param {JsonObject} customization customization parameter object
 * @param {string} client client identifier ('telma' or 'hgc')
 * @param {string} requestedMode color mode; HGC has no dark variant and always renders light
 */

export const theme = (
    customization: LayoutSlice,
    client: Client = 'telma',
    requestedMode: PaletteMode = 'light',
) => {
    const mode = client === 'hgc' ? 'light' : requestedMode;
    const color = getClientColors(client, mode);

    const themeOption = {
        mode,
        colors: color,
        heading: color.grey900,
        paper: color.paper,
        backgroundDefault: color.paper,
        background: color.primaryLight,
        bgPinkDark: color.secondaryBgPinkDark,
        bgPinkHover: color.secondaryBgPinkHover,
        whiteTextPrimary: color.primaryLight,
        darkTextPrimary: color.grey700,
        darkTextSecondary: color.grey500,
        textDark: color.grey900,
        menuSelected: color.secondaryDark,
        menuSelectedBack: color.secondaryLight,
        divider: color.grey200,
        customShadows: buildCustomShadows(color.primaryDark),
        customization,
    };

    const themeOptions: ThemeOptions = {
        direction: 'ltr',
        //@ts-ignore
        palette: themePalette(themeOption),
        mixins: {
            toolbar: {
                minHeight: '48px',
                '@media (min-width: 600px)': {
                    minHeight: '48px',
                },
            },
            //@ts-ignore
            drawer: {
                width: 250,
            },
        },
        //@ts-ignore
        typography: themeTypography(themeOption),
        borderRadius: 12,
        customShadows: themeOption.customShadows,
    };

    const themes = createTheme(themeOptions);
    themes.components = componentStyleOverrides(themeOption);

    return themes;
};

export default theme;
